import React from 'react';
import { Link } from 'react-router-dom';
import { getPartyTextColor, getPartyAbbreviation } from '../utils/partyUtils';

interface BillSponsor {
  name: string;
  title: string;
  type?: string;
  sponsor_type?: string;
  party?: string;
  legislator_id?: string;
  chamber?: string;
}

interface SponsorDisplayProps {
  sponsors: BillSponsor[];
  className?: string;
  /** If true, links to public legislator pages instead of authenticated pages */
  isPublic?: boolean;
  /** Bill number (e.g. HB25-1001, SB25-200). When set, show originating chamber first: House for HB, Senate for SB */
  billNumber?: string;
}

const SponsorDisplay: React.FC<SponsorDisplayProps> = ({ sponsors, className = '', isPublic = false, billNumber }) => {
  if (!sponsors || sponsors.length === 0) {
    return <span className="text-text-tertiary dark:text-text-dark-tertiary">No sponsors</span>;
  }

  // Deduplicate sponsors based on legislator_id or name+chamber combination
  const deduplicatedSponsors = sponsors.reduce((acc: BillSponsor[], sponsor) => {
    const isDuplicate = acc.some(existing => {
      // If both have legislator_id, compare by that
      if (sponsor.legislator_id && existing.legislator_id) {
        return sponsor.legislator_id === existing.legislator_id;
      }
      // Otherwise, compare by name and chamber
      return existing.name?.trim().toLowerCase() === sponsor.name?.trim().toLowerCase() &&
             existing.chamber?.toLowerCase() === sponsor.chamber?.toLowerCase();
    });
    
    if (!isDuplicate) {
      acc.push(sponsor);
    }
    return acc;
  }, []);

  const primeSponsors = deduplicatedSponsors.filter(s => s?.sponsor_type === 'Prime Sponsor' || s?.type === 'Prime Sponsor');
  const coSponsors = deduplicatedSponsors.filter(s => s?.sponsor_type !== 'Prime Sponsor' && s?.type !== 'Prime Sponsor');

  // Group prime sponsors by chamber
  const houseSponsors = primeSponsors.filter(s => s.chamber && s.chamber.toLowerCase() === 'house');
  const senateSponsors = primeSponsors.filter(s => s.chamber && s.chamber.toLowerCase() === 'senate');
  const unknownChamberSponsors = primeSponsors.filter(s => !s.chamber || 
    (s.chamber.toLowerCase() !== 'house' && s.chamber.toLowerCase() !== 'senate'));

  // Order by originating house: HB -> House first, SB -> Senate first
  const prefix = (billNumber || '').trim().toUpperCase().slice(0, 2);
  const houseFirst = prefix === 'HB' || prefix !== 'SB';

  const firstChamberLabel = houseFirst ? 'House' : 'Senate';
  const secondChamberLabel = houseFirst ? 'Senate' : 'House';
  const firstChamberSponsors = houseFirst ? houseSponsors : senateSponsors;
  const secondChamberSponsors = houseFirst ? senateSponsors : houseSponsors;

  return (
    <div className={className}>
      {primeSponsors.length > 0 && (
        <div className="flex flex-wrap items-baseline">
          {/* First chamber (originating for HB/SB) */}
          {firstChamberSponsors.length > 0 && (
            <span className="mr-1">
              <span className="font-semibold text-text-primary dark:text-text-dark-primary">{firstChamberLabel}:</span>
              {' '}
              {firstChamberSponsors.map((sponsor, index) => (
                <React.Fragment key={`first-${index}`}>
                  {index > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary">, </span>}
                  {sponsor.legislator_id ? (
                    <Link 
                      to={isPublic ? `/public/legislators/${sponsor.legislator_id}` : `/legislators/${sponsor.legislator_id}`}
                      className={`hover:underline cursor-pointer ${getPartyTextColor(sponsor.party)}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sponsor.name?.trim()}
                      {getPartyAbbreviation(sponsor.party) && ` (${getPartyAbbreviation(sponsor.party)})`}
                    </Link>
                  ) : (
                    <span className={getPartyTextColor(sponsor.party)}>
                      {sponsor.name?.trim()}
                      {getPartyAbbreviation(sponsor.party) && ` (${getPartyAbbreviation(sponsor.party)})`}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          )}
          
          {/* Separator between first and second chamber */}
          {firstChamberSponsors.length > 0 && secondChamberSponsors.length > 0 && (
            <span className="text-text-tertiary dark:text-text-dark-tertiary mr-1">; </span>
          )}
          
          {/* Second chamber */}
          {secondChamberSponsors.length > 0 && (
            <span className="mr-1">
              <span className="font-semibold text-text-primary dark:text-text-dark-primary">{secondChamberLabel}:</span>
              {' '}
              {secondChamberSponsors.map((sponsor, index) => (
                <React.Fragment key={`second-${index}`}>
                  {index > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary">, </span>}
                  {sponsor.legislator_id ? (
                    <Link 
                      to={isPublic ? `/public/legislators/${sponsor.legislator_id}` : `/legislators/${sponsor.legislator_id}`}
                      className={`hover:underline cursor-pointer ${getPartyTextColor(sponsor.party)}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sponsor.name?.trim()}
                      {getPartyAbbreviation(sponsor.party) && ` (${getPartyAbbreviation(sponsor.party)})`}
                    </Link>
                  ) : (
                    <span className={getPartyTextColor(sponsor.party)}>
                      {sponsor.name?.trim()}
                      {getPartyAbbreviation(sponsor.party) && ` (${getPartyAbbreviation(sponsor.party)})`}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          )}
          
          {/* Separator if there are unknown chamber sponsors */}
          {(firstChamberSponsors.length > 0 || secondChamberSponsors.length > 0) && unknownChamberSponsors.length > 0 && (
            <span className="text-text-tertiary dark:text-text-dark-tertiary mr-1">; </span>
          )}
          
          {/* Unknown Chamber Sponsors */}
          {unknownChamberSponsors.length > 0 && (
            <span className="mr-1">
              {unknownChamberSponsors.map((sponsor, index) => (
                <React.Fragment key={`unknown-${index}`}>
                  {index > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary">, </span>}
                  {sponsor.legislator_id ? (
                    <Link 
                      to={isPublic ? `/public/legislators/${sponsor.legislator_id}` : `/legislators/${sponsor.legislator_id}`}
                      className={`hover:underline cursor-pointer ${getPartyTextColor(sponsor.party)}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sponsor.name?.trim()}
                      {getPartyAbbreviation(sponsor.party) && ` (${getPartyAbbreviation(sponsor.party)})`}
                    </Link>
                  ) : (
                    <span className={getPartyTextColor(sponsor.party)}>
                      {sponsor.name?.trim()}
                      {getPartyAbbreviation(sponsor.party) && ` (${getPartyAbbreviation(sponsor.party)})`}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          )}
        </div>
      )}
      
      {coSponsors.length > 0 && (
        <div className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          (+{coSponsors.length} co-sponsor{coSponsors.length > 1 ? 's' : ''})
        </div>
      )}
    </div>
  );
};

export default SponsorDisplay;
